import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { canOfferFirebaseAnalytics } from '@/lib/firebase/config';
import {
  ANALYTICS_CONSENT_STORAGE_KEY,
  type AnalyticsConsentStatus,
  getAnalyticsConsentStatus,
  persistAnalyticsConsentStatus,
} from '@/lib/firebase/consent';

type AnalyticsConsentContextValue = {
  status: AnalyticsConsentStatus;
  canOfferAnalytics: boolean;
  isPreferencesOpen: boolean;
  grantConsent: () => void;
  denyConsent: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
};

const AnalyticsConsentContext = createContext<AnalyticsConsentContextValue | null>(null);

type AnalyticsConsentProviderProps = {
  children: ReactNode;
  canOfferAnalytics?: boolean;
};

export const AnalyticsConsentProvider = ({
  children,
  canOfferAnalytics = canOfferFirebaseAnalytics(),
}: AnalyticsConsentProviderProps) => {
  const [status, setStatus] = useState<AnalyticsConsentStatus>(() =>
    getAnalyticsConsentStatus()
  );
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  useEffect(() => {
    if (!canOfferAnalytics || typeof window === 'undefined') {
      return;
    }

    const synchronizeConsent = (event: StorageEvent) => {
      if (
        event.storageArea === window.localStorage &&
        (event.key === ANALYTICS_CONSENT_STORAGE_KEY || event.key === null)
      ) {
        setStatus(getAnalyticsConsentStatus());
      }
    };

    window.addEventListener('storage', synchronizeConsent);
    return () => window.removeEventListener('storage', synchronizeConsent);
  }, [canOfferAnalytics]);

  const updateConsent = useCallback((nextStatus: 'granted' | 'denied') => {
    persistAnalyticsConsentStatus(nextStatus);
    setStatus(nextStatus);
    setIsPreferencesOpen(false);
  }, []);

  const value = useMemo<AnalyticsConsentContextValue>(
    () => ({
      status,
      canOfferAnalytics,
      isPreferencesOpen,
      grantConsent: () => updateConsent('granted'),
      denyConsent: () => updateConsent('denied'),
      openPreferences: () => setIsPreferencesOpen(true),
      closePreferences: () => setIsPreferencesOpen(false),
    }),
    [canOfferAnalytics, isPreferencesOpen, status, updateConsent]
  );

  return (
    <AnalyticsConsentContext.Provider value={value}>
      {children}
    </AnalyticsConsentContext.Provider>
  );
};

export const useAnalyticsConsent = (): AnalyticsConsentContextValue => {
  const context = useContext(AnalyticsConsentContext);

  if (!context) {
    throw new Error('useAnalyticsConsent must be used within AnalyticsConsentProvider');
  }

  return context;
};
